import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { merge } from 'lodash';
import { DeepPartial } from 'redux';
import { BusinessCustomerTypesEnum } from '../../../screens/NewQuotation/CustomerDataPage/customerDataPage.model';
import { CustomerTypeEnum } from '../../../types/customerTypeSelection';
import { ProductFamilyEnum } from '../../../types/productFamilySelection';
import { VehicleType } from '../../../types/vehicleTypeSelect';

/**
 * - Data in form compatible with BUS models and recognized by the business.
 * - The model right now is completely made-up since we don't yet know official mapping.
 *
 * - Data is added here in 2 ways:
 * 1) via prefill from data storage so that all forms can be also hydrated
 * 2) by forms themselves as they are being edited
 *
 * - Since this module is both source of forms prefill and reflection of their
 * current state, take care to avoid reactive circular references.
 */
export type JourneyDataModel = {
  address: {
    timeAtAddressFrom?: string;
  };
  customerType?: CustomerTypeEnum;
  businessCustomerType?: BusinessCustomerTypesEnum;
  productFamily?: ProductFamilyEnum;
  vehicleType?: VehicleType;
  secondHirer: {
    address: {
      timeAtAddressFrom?: string;
    };
  };
  snCInitialization?: {
    email?: string;
    countryCode?: string;
    mobileNumber?: string;
  };
  isAdditionalServicePlanAdded: boolean;
  additionalServicePlan: {
    paymentType?: string;
  };
  standaloneServicePlan: {
    paymentType?: string;
  };
  quoteComparison: {
    numberOfChosenQuotes: number;
    numberOfChosenQuotesLocked: boolean;
  };
};

export type JourneyModelAddress = JourneyDataModel['address'];

const initialState: Partial<JourneyDataModel> = {
  isAdditionalServicePlanAdded: true,
  standaloneServicePlan: {
    paymentType: '',
  },
  quoteComparison: {
    numberOfChosenQuotes: 1,
    numberOfChosenQuotesLocked: false,
  },
};

export const slice = createSlice({
  name: 'journeyData',
  initialState,
  selectors: {
    address: (state) => state.address,
    secondHirerAddress: (state) => state.secondHirer?.address,
    customerType: (state) => state.customerType,
    businessCustomerType: (state) => state.businessCustomerType,
    productFamily: (state) => state.productFamily,
    vehicleType: (state) => state.vehicleType,
    snCInitialization: (state) => state.snCInitialization,
    isAdditionalServicePlanAdded: (state) => state.isAdditionalServicePlanAdded,
    additionalServicePlan: (state) => state.additionalServicePlan,
    isUpfrontPayment: (state) => state.standaloneServicePlan?.paymentType?.toLowerCase() === 'upfront',
    numberOfChosenQuotes: (state) => state.quoteComparison?.numberOfChosenQuotes,
    numberOfChosenQuotesLocked: (state) => state.quoteComparison?.numberOfChosenQuotesLocked,
  },
  reducers: {
    updateData: (state, action: PayloadAction<DeepPartial<JourneyDataModel>>) => {
      state = merge(state, action.payload);
    },
    setCustomerType: (state, action: PayloadAction<CustomerTypeEnum | undefined>) => {
      state.customerType = action.payload;
    },
    setBusinessCustomerType: (state, action: PayloadAction<BusinessCustomerTypesEnum | undefined>) => {
      state.businessCustomerType = action.payload;
    },
    setProductFamily: (state, action: PayloadAction<ProductFamilyEnum | undefined>) => {
      state.productFamily = action.payload;
    },
    setVehicleType: (state, action: PayloadAction<VehicleType | undefined>) => {
      state.vehicleType = action.payload;
    },
    setIsAdditionalServicePlanAdded: (state, action: PayloadAction<boolean>) => {
      state.isAdditionalServicePlanAdded = action.payload;
    },
    setStandaloneServicePlanPaymentType: (state, action: PayloadAction<string>) => {
      if (state.standaloneServicePlan) {
        state.standaloneServicePlan.paymentType = action.payload;
      }
    },
    increaseNumberOfChosenQuotes: (state) => {
      if (state.quoteComparison) {
        state.quoteComparison.numberOfChosenQuotes++;
      }
    },
    clearNumberOfChosenQuotes: (state) => {
      if (state.quoteComparison) {
        state.quoteComparison.numberOfChosenQuotes = 0;
      }
    },
    setNumberOfChosenQuotesLocked: (state, action: PayloadAction<boolean>) => {
      if (state.quoteComparison) {
        state.quoteComparison.numberOfChosenQuotesLocked = action.payload;
      }
    },
    resetJourneyData: () => initialState,
  },
});

export const journeyDataSlice = slice;
export const journeyDataActions = slice.actions;
export const journeyDataSelectors = slice.selectors;
